// Ported from vlookup-dramatic/components/ui/select.tsx without Base UI.
// A simple custom dropdown with the same component names:
// <Select value={v} onValueChange={setV}><SelectTrigger><SelectValue placeholder="…"/></SelectTrigger>
//   <SelectContent><SelectItem value="a">A</SelectItem></SelectContent></Select>
// The popup renders inside the page (no portal) — keep the trigger's ancestors
// free of transform/backdrop-filter, or use a Dialog instead.
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import { ChevronDownIcon, CheckIcon } from "../../lib/icons";

const Ctx = createContext(null);

export function Select({ value, defaultValue, onValueChange, children }) {
  const [internal, setInternal] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const [labels, setLabels] = useState({});
  const current = value !== undefined ? value : internal;
  const setValue = (v) => {
    if (value === undefined) setInternal(v);
    if (onValueChange) onValueChange(v);
    setOpen(false);
  };
  const register = (v, label) =>
    setLabels((l) => (l[v] === label ? l : { ...l, [v]: label }));
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <Ctx.Provider value={{ value: current, setValue, open, setOpen, labels, register }}>
      <div ref={rootRef} data-slot="select" className="relative">
        {children}
      </div>
    </Ctx.Provider>
  );
}

export function SelectGroup({ className, ...props }) {
  return <div data-slot="select-group" className={cn("scroll-my-1 p-1", className)} {...props} />;
}

export function SelectValue({ className, placeholder }) {
  const { value, labels } = useContext(Ctx);
  const text = value !== undefined && value !== null && value !== "" ? labels[value] || value : null;
  return (
    <span data-slot="select-value" data-placeholder={text ? undefined : ""} className={cn("flex flex-1 text-left", className)}>
      {text || placeholder}
    </span>
  );
}

export function SelectTrigger({ className, size = "default", children, ...props }) {
  const { open, setOpen, value } = useContext(Ctx);
  return (
    <button
      type="button"
      data-slot="select-trigger"
      data-size={size}
      data-placeholder={value ? undefined : ""}
      aria-haspopup="listbox"
      aria-expanded={open}
      onClick={() => setOpen(!open)}
      className={cn(
        "flex w-fit items-center justify-between gap-1.5 rounded-lg border border-input bg-transparent py-2 pr-2 pl-2.5 text-sm whitespace-nowrap transition-colors outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 data-placeholder:text-muted-foreground data-[size=default]:h-8 data-[size=sm]:h-7 data-[size=sm]:rounded-[min(var(--radius-md),10px)] [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      {children}
      <ChevronDownIcon className="pointer-events-none size-4 text-muted-foreground" />
    </button>
  );
}

export function SelectContent({ className, children, ...props }) {
  const { open } = useContext(Ctx);
  return (
    <div
      role="listbox"
      data-slot="select-content"
      hidden={!open}
      className={cn(
        "absolute left-0 top-full z-50 mt-1 max-h-72 w-full min-w-36 overflow-x-hidden overflow-y-auto rounded-lg bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function SelectLabel({ className, ...props }) {
  return <div data-slot="select-label" className={cn("px-1.5 py-1 text-xs text-muted-foreground", className)} {...props} />;
}

export function SelectItem({ className, value, children, ...props }) {
  const { value: current, setValue, register } = useContext(Ctx);
  const label = typeof children === "string" ? children : undefined;
  useEffect(() => {
    if (label !== undefined) register(value, label);
  }, [value, label, register]);
  const selected = current === value;
  return (
    <div
      role="option"
      aria-selected={selected}
      data-slot="select-item"
      data-selected={selected ? "true" : undefined}
      onClick={() => setValue(value)}
      className={cn(
        "relative flex w-full cursor-default items-center gap-1.5 rounded-md py-1 pr-8 pl-1.5 text-sm outline-hidden select-none hover:bg-accent hover:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      <span className="flex flex-1 shrink-0 gap-2 whitespace-nowrap">{children}</span>
      {selected && (
        <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center">
          <CheckIcon className="pointer-events-none" />
        </span>
      )}
    </div>
  );
}

export function SelectSeparator({ className, ...props }) {
  return <div data-slot="select-separator" className={cn("pointer-events-none -mx-1 my-1 h-px bg-border", className)} {...props} />;
}
